import type { IInternetTestHistory, IInternetTestDetails, IHardware, IWorkHistory } from '@/store/types';

export interface IResumeBuilderInitialState {
  activeStep: number;
  showSuccessModal: boolean;
  activeStepName: string;
  contact?: IContact | undefined;
  internetTestHistory: IInternetTestHistory[];
  testStatus: ITestStatus[] | [];
  workhistory: IWorkHistory[] | [];
  profilePicture?: IProfilePicture;
  hardwareList: IHardware[] | [];
  hardwareTestToken: string;
  hardwareDetail: IHardware;
  voiceQuestionList: IVoiceQuestion[] | [];
  voiceAnswers: IVoiceAnswer[] | [];
  applicationFlags: IApplicationFlags;
  applicationFlagsLoading: boolean;
}

export interface IProfilePicture {
  path: string;
  link: string;
  type: string;
  dateUploaded: number;
  approvalStatus: string;
  profilePictureId: number;
}

export interface ITestStatus {
  client?: string;
  application_requirement_status_id: number;
  gig: string;
}

export interface ISystem {
  system: ISystemDetails | undefined;
}

export interface IVoiceQuestion {
  gigId: number;
  voicequestion: {
    voicequestionId: number;
    questionText: string;
    maxResponseInSeconds: number;
    clientId: number;
    listOrder: number;
  };
  voicequestionId: number;
}

export interface IVoiceAnswer {
  applicationId: number;
  voicequestionId: number;
  uploadTimeUnix: number;
  blobUrl: string;
  blobname: string;
}

interface IDisplayDevice {
  Availability: string;
  Description: string;
  DeviceID: string;
  DisplayType: string | null;
  MonitorManufacturer: string | null;
  MonitorType: string;
  Name: string;
  PixelsPerXLogicalInch: string;
  PixelsPerYLogicalInch: string;
  ScreenHeight: string | null;
  ScreenWidth: string | null;
  Status: string;
}

interface ISoundDevice {
  description: string;
  manufacturer: string;
  model: string;
  name: string;
}

interface IVideoDevice {
  description: string;
  manufacturer: string | null;
  name: string;
  ram: string;
  resolution: string;
}

interface IBiosDevice {
  description: string;
  manufacturer: string;
  releasedate: string;
  serial: string;
  smversion: string;
  version: string;
}
interface INetworkDevice {
  adaptertype: string;
  adaptertypeID: string;
  availability: string;
  description: string;
  manufacturer: string;
  maxspeed: null;
  model: string;
  netconnectionstatus: string;
  netenabled: string;
  physicaladapter: string;
  speed: string;
}

interface IProcessorDevice {
  architecture: string;
  core_count: string;
  description: string;
  eps: string;
  logical_count: string;
  manufacturer: string;
  mflops: string;
  mflops_cores: string;
  speed: string;
}

interface IWindowsDevice {
  description: string | null;
  free_physical_memory: string;
  os_arch: string;
  os_lang: string;
  os_name: string;
  os_serial: string;
  os_sku: string;
  os_sp: string;
  os_spm: string;
  os_type: string;
  os_version: string;
}

export interface ISystemDetails {
  memory: {
    total_memory: string;
  };
  bios: {
    device: IBiosDevice | IBiosDevice[];
  };
  display: {
    device: IDisplayDevice[] | IDisplayDevice;
  };
  network: {
    device: INetworkDevice | INetworkDevice[];
  };
  processor: {
    device: IProcessorDevice | IProcessorDevice[];
  };
  sound: {
    device: ISoundDevice | ISoundDevice[];
  };
  video: {
    device: IVideoDevice | IVideoDevice[];
  };
  windows: {
    device: IWindowsDevice | IWindowsDevice[];
  };
}

export interface IStepStatusResponse {
  meta?: {
    arg: IStepStatus;
    requestId: string;
    requestStatus: string;
  };
  type?: string;
}

export const stepLabelToIds: IStepLabelToIds = {
  contact: 1,
  mobileNumber: 2,
  educationAndLanguage: 3,
  workHistory: 4,
  voiceScript: 5,
  acknowledgements: 6,
  profilePhoto: 7,
  internetTest: 8,
  computerTest: 9,
  equipment: 10,
  gst: 11,
  backgroundCheck: 12,
};

export interface IStepLabelToIds {
  [key: string]: number;
}

export const activeStepIdToName: IActiveStepIdToName = {
  1: 'contact',
  2: 'mobileNumber',
  3: 'profilePhoto',
  4: 'educationAndLanguage',
  5: 'workHistory',
  6: 'voiceScript',
  7: 'acknowledgements',
  9: 'internetTest',
  10: 'computerTest',
  11: 'equipment',
  12: 'gst',
  13: 'backgroundCheck',
};

export interface IActiveStepIdToName {
  [key: number]: string;
}

export enum stepLables {
  contact = 'contact',
  shippingAddress = 'shippingAddress',
  profilePhoto = 'profilePhoto',
  skills = 'skills',
  workHistory = 'workHistory',
  voiceScript = 'voiceScript',
  equipment = 'equipment',
  internetTest = 'internetTest',
  computerTest = 'computerTest',
}
export interface IRBStepComponentProps {
  handleNext: () => void;
  handleBack: () => void;
}

export interface IRBStage2ComponentProps {
  handleNext?: () => void;
  hideNextBackButtons?: boolean;
  handleBack?: () => void;
  hideRuntestSection?: boolean;
  isAppSpecificTest?: boolean;
  isStepConfig?: boolean;
  spUserId?: number;
}

export interface IWorkForm {
  companyName: string;
  employedFrom: string;
  employedTo: string;
  jobTitle: string;
  location: string;
  jobDescription: string;
}

export interface IContact {
  dateOfBirth?: string;
  email?: string;
  firstName?: string;
  forceSave?: boolean;
  forwardMail?: boolean;
  genderId?: number;
  isValidated?: boolean;
  languageType?: string;
  lastName?: string;
  loginMode?: string;
  lxEmail?: string;
  lxUser?: string;
  phoneAuthNum?: string;
  primaryLanguageId?: number;
  profilePictureApproval?: string;
  profilePicturePath?: string;
  profilePictureUploadTimestamp?: string;
  shippingCity?: string;
  shippingCountry?: number;
  shippingIsValidated?: string;
  shippingLastUpdated?: string;
  shippingPostal?: string;
  shippingProvince?: number;
  shippingStreet_1?: string;
  shippingStreet_2?: string;
  userId?: number;
  notifySms?: number;
  phoneSMS?: string;
}

export interface IStepStatus {
  isSupportedDevice?: boolean;
  applicationId: number;
  createdTimestamp?: number;
  stepId: number;
  stepStatusId: number;
  vocabApplicationStep?: string;
  vocabApplicationStepStatus?: { stepStatusId: number; label: string };
}

export interface ITestStatusPayLoad {
  requirementType: number;
  userId?: number;
}
export interface IApplicationFlags {
  applicationId: number;
  hasWorkHistory: boolean;
  ackShippingAddress: boolean;
  ackFailedShipmentFee: boolean;
  ackBaseHours: boolean;
  ackPeakHours: boolean;
  ackTax: boolean;
  ackBackgroundCheck: boolean;
  campaignId: number;
  userId: number;
  agentContractId: number;
  applicationStatusId: number;
  createdTimestamp: number;
  createdDatetime: string;
  countryId: number;
  modifiedTimestamp?: string;
  modifiedDatetime?: string;
  campaign?: {
    gigId: number;
  };
}

export interface IApplicationPayload {
  application: IApplicationFlags;
  currentStatusId?: number;
}

export interface IInternetTestDetailsModal {
  testDetails: IInternetTestDetails | undefined;
  show: boolean;
  onHide: () => void;
  onExited: () => void;
  showTestAgain: boolean;
  onTestShow?: () => void;
}

export interface IInternetTestDetailsGenericModal extends IInternetTestDetailsModal {
  showTestAgain: boolean;
  onTestShow?: () => void;
}

export interface IInternetTestResultDetails {
  testDetails: IInternetTestDetails | undefined;
  canShowTitle?: boolean;
  canShowGigName?: boolean;
}

export interface IEducationOptions {
  educationLevelId: number;
  label: string;
  listOrder: number;
  visibility: boolean;
}

export interface ILanguageOptions {
  labelIso?: string;
  labelLong: string;
  labelNative?: string;
  labelShort?: string;
  languageId: number;
  listOrder?: number;
  visibility?: boolean;
}

export interface IProficiencyOptions {
  label: string;
  listOrder?: number;
  proficiencyId: number;

  visibility?: boolean;
}
export interface IProficiencyAreaOptions {
  label: string;
  listOrder?: number;

  proficiencyAreaId: number;
  visibility?: boolean;
}

export interface IProficiencyProps {
  editComponent: boolean;
}

export interface IProficiencyComponent {
  languages: ILanguageOptions[];
  proficiencies: IProficiencyOptions[];
  proficiencyArea: IProficiencyAreaOptions[];
  memberProficiencies: ILanguageAndProficiency[] | undefined;
  tableHeading: string;
  onProficiencyUpdated: (payload: ILanguageAndProficiency[]) => void;
}

export interface IMemberProficiencyArea {
  proficiencyAreaId: number;
  proficiencyId: number;
}
export interface IMemberProficiencies {
  languageId: number;
  proficiencies: IMemberProficiencyArea[];
}

export interface ILanguageAndProficiency {
  languageId: number;
  lxUser?: {
    userId: number;
  };
  userId?: number;

  proficiencies: {
    dateCreated?: string;
    dateModified?: string;
    proficiencyAreaId: number;
    proficiencyAreaLabel?: string;
    proficiencyId: number;
    proficiencyLabel?: string;
  }[];
}

export interface ISetProficiency {
  LanguageId: number;
  Proficiencies: {
    ProficiencyAreaId: number;
    ProficiencyId: number;
  }[];
}

export interface IProficiencyTable {
  tableHeading: string;
  memberProficiencies: ILanguageAndProficiency[];
  proficiencyArea: IProficiencyAreaOptions;
  onProficiencyUpdated: (prof: ILanguageAndProficiency[]) => void;
}

export interface ISkillsSetter {
  educationLevelId: number;
  callCenterExperienceId: number;
}

export interface IEducation {
  educationId?: number;
  dateCreated?: string;
  dateModified?: string;
  educationLevelId: number;
  userId: number;
  lxUser?: {
    userId: number;
  };
  vocabEducationLevel?: IEducationOptions;
}

export interface IProfilePicturePayload {
  file: File;
  id: number;
}

export interface ISkillsInitialState {
  educationOptions: IEducationOptions[];
  education: IEducation | undefined;
  educationId: number;
  languageAndProficiency: {
    languageOptions: ILanguageOptions[];
    proficiencyAreasOptions: IProficiencyAreaOptions[];
    proficiencyOptions: IProficiencyOptions[];
    userProficiencies: ILanguageAndProficiency[];
    userProficienciesCopy: ILanguageAndProficiency[];
  };
}

export interface IUserProficiencies {
  education: IEducation | undefined;
  languageProficiencies: ILanguageAndProficiency[];
}

export interface ISpeedTestResult {
  applicationId: number | null;
  testId: number;
  guid: string;
  memberId: number | null;
  userId: number | null;
  vJit: string;
  vDjit: string;
  vMos: string;
  vDmos: string;
  vLoss: string;
  vDloss: string;
  vRttMin: string;
  cDown: string;
  cUp: string;
  cDpkt: string;
  cUpkt: string;
  conType: string;
  dCos: string;
  uCos: string;
  cQoS: string;
  routeSpeed: string;
  rttMin: string;
  rttMax: string;
  rttAvg: string;
  rttConsistency: string;
  bandwidth: string;
  vRttAvg: string;
  vRttMax: string;
  vRttConsistency: string;
  vLossDist: string;
  vDlossDist: string;
  dSpeed: string;
  uSpeed: string;
  ip: string;
  mode: string;
  detailLink: string;
  voipLink: string;
  analysisLink: string;
}
